<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'payments',
    path: '/payments',
    component: 'Payments',
    title: () => pgettext('PaymentsView', 'Payments'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Payments',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    parameters: {
      type: Object,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getItems = (...args) => {
    if (props.parameters) {
      return $fn.value.fetch('list_payment', { parameters: props.parameters });
    }

    const parameters = params.value;
    return parameters && parameters.account && parameters.date_from
      ? $fn.value.fetch('list_payment', { parameters })
      : [];
  };
  const getInvoiceBadgeClass = (...args) => {
    const data = args[0];

    if (_.isEmpty(data)) {
      return;
    }

    const payment_balance = data.payment_balance || 0;
    const invoice_gross_amount = data.invoice_gross_amount;

    if (payment_balance == 0) {
      return 'bg-green-200';
    }

    if (payment_balance < 0) {
      return 'bg-yellow-200';
    }

    if (payment_balance < invoice_gross_amount) {
      return 'bg-blue-200';
    }
  };

  const selectedItems = ref([]);
  const params = ref({});
  const totals = ref([
    {
      id: 0,
      amount_in: 0,
      amount_out: 0,
    },
  ]);
  const paymentMethods = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_6466705138206980_headerShowColumnSelectorButton = computed(
    () => false
  );
  const b_6466705138206980_headerShowReloadButton = computed(() => false);
  const b_6466705138206980_columnDefaults = computed(() => ({
    sortable: false,
    filterable: true,
    resizable: true,
  }));
  const b_6466705138206980_columns = computed(() => [
    {
      field: 'payment_method',
      name: $fn.value.pgettext('PaymentBrowserView', 'Method'),
      formatter: {
        name: 'gettext',
        context: 'PaymentMethod',
        conversion: ['Unsnake', 'Sentence'],
        filterInput: true,
      },
    },
    {
      field: 'transaction_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Number'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'transaction_type',
      name: $fn.value.pgettext('PaymentBrowserView', 'Type'),
      formatter: {
        name: 'gettext',
        context: 'TransactionType',
        conversion: 'Unsnake',
        filterInput: true,
      },
    },
    {
      field: 'transaction_date',
      name: $fn.value.pgettext('PaymentBrowserView', 'Date'),
      type: 'date',
    },
    {
      field: 'transaction_title',
      name: $fn.value.pgettext('PaymentBrowserView', 'Title'),
    },
    {
      field: 'transaction_code',
      name: $fn.value.pgettext('PaymentBrowserView', 'Transaction code'),
    },
    {
      field: 'statement_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Statement number'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('PaymentBrowserView', 'Description'),
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('PaymentBrowserView', 'Message'),
    },
    {
      field: 'opening_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Opening balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'amount_in',
      name: $fn.value.pgettext('PaymentBrowserView', 'In'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'amount_out',
      name: $fn.value.pgettext('PaymentBrowserView', 'Out'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'closing_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Closing balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('PaymentBrowserView', 'Currency'),
    },
    {
      field: 'exchange_rate',
      name: $fn.value.pgettext('PaymentBrowserView', 'Exchange Rate'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'reference_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Ref Nr.'),
    },
    {
      field: 'file_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'File Nr.'),
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('PaymentBrowserView', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'invoices',
      name: $fn.value.pgettext('PaymentBrowserView', 'Invoices'),
      formatter: [
        {
          name: 'chain',
          mode: 'mapReduce',
          separator: ', ',
          formatterContext: 'value',
          formatters: [
            {
              name: 'getvalue',
              opts: {
                field: 'invoice_number',
                source: 'value',
              },
            },
            {
              name: 'routeLink',
              to: 'invoice',
              navigateOnIconClick: true,
              params: {
                id: 'id',
              },
            },
            {
              name: 'badge',
              badgeClass: getInvoiceBadgeClass,
            },
          ],
        },
      ],
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    params: params.value,
    totals: totals.value,
    paymentMethods: paymentMethods.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_6466705138206980_headerShowColumnSelectorButton:
      b_6466705138206980_headerShowColumnSelectorButton.value,
    b_6466705138206980_headerShowReloadButton:
      b_6466705138206980_headerShowReloadButton.value,
    b_6466705138206980_columnDefaults: b_6466705138206980_columnDefaults.value,
    b_6466705138206980_columns: b_6466705138206980_columns.value,
  }));

  const $$load_paymentMethods = async () => {
    return $fn.value
      .fetch('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'params' },
      { name: 'totals' },
      { name: 'paymentMethods' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      {
        name: 'b_6466705138206980_headerShowColumnSelectorButton',
        nodeId: '6466705138206980',
      },
      {
        name: 'b_6466705138206980_headerShowReloadButton',
        nodeId: '6466705138206980',
      },
      { name: 'b_6466705138206980_columnDefaults', nodeId: '6466705138206980' },
      { name: 'b_6466705138206980_columns', nodeId: '6466705138206980' },
    ],
    loaders: { paymentMethods: $$load_paymentMethods },
    ux: {},
    dataRefs: {
      selectedItems,
      params,
      totals,
      paymentMethods,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_6466705138206980_headerShowColumnSelectorButton,
      b_6466705138206980_headerShowReloadButton,
      b_6466705138206980_columnDefaults,
      b_6466705138206980_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('PaymentsView', 'Payments'),
      icon: $config.value.payment.paymentsList.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_cytnijbc7_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_setData_s2rnpemea_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_6466705138206980_showModal_lkqozadbq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_6466705138206980_command_nhlcuurn4_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'transaction_number';
  };
  const act_7046827037756168_setData_ogtx1w1l2_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'params',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7046827037756168_script_2u30oma3k_value_callable = async (
    $event
  ) => {
    return $refs.grid.reload();
  };
  const act_7046827037756168_script_2u30oma3k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7046827037756168_script_2u30oma3k_value_callable($event);
      },
    };
  };

  const act_7315092382398562_closeModal_cytnijbc7 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_cytnijbc7_getActionArgs,
    });
  const inst_evh_7315092382398562_close_0jjyjui79 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_cytnijbc7,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_0jjyjui79],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ofovopdhf =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_w4x1g20go =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ofovopdhf,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_w4x1g20go],
    event: 'reload',
  });
  const act_6466705138206980_setData_s2rnpemea = new actionTypes.SetDataAction({
    actionArgs: act_6466705138206980_setData_s2rnpemea_getActionArgs,
  });
  const inst_evh_6466705138206980_selectedRowsChanged_9pkdkvycs =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_setData_s2rnpemea,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_6466705138206980_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_selectedRowsChanged_9pkdkvycs],
      event: 'selectedRowsChanged',
    });
  const act_6466705138206980_showModal_lkqozadbq =
    new actionTypes.ShowModalAction({
      actionArgs: act_6466705138206980_showModal_lkqozadbq_getActionArgs,
    });
  const inst_evh_6466705138206980_command_nhlcuurn4 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_showModal_lkqozadbq,
      event: 'command',
      when: inst_evh_6466705138206980_command_nhlcuurn4_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_6466705138206980_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_command_nhlcuurn4],
    event: 'command',
  });
  const act_7046827037756168_setData_ogtx1w1l2 = new actionTypes.SetDataAction({
    actionArgs: act_7046827037756168_setData_ogtx1w1l2_getActionArgs,
  });
  const inst_evh_7046827037756168_submit_mrzhbjuvm =
    new actionTypes.EventHandlerImpl({
      action: act_7046827037756168_setData_ogtx1w1l2,
      event: 'submit',
      displayName: 'setData',
    });
  const act_7046827037756168_script_2u30oma3k = new actionTypes.ScriptAction({
    actionArgs: act_7046827037756168_script_2u30oma3k_getActionArgs,
  });
  const inst_evh_7046827037756168_submit_x1lgnahb9 =
    new actionTypes.EventHandlerImpl({
      action: act_7046827037756168_script_2u30oma3k,
      event: 'submit',
      displayName: 'script',
    });
  const inst_evh_7046827037756168_submit = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7046827037756168_submit_mrzhbjuvm,
      inst_evh_7046827037756168_submit_x1lgnahb9,
    ],
    event: 'submit',
  });

  const actions = {
    inst_evh_7315092382398562_close_0jjyjui79,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_w4x1g20go,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_selectedRowsChanged_9pkdkvycs,
    inst_evh_6466705138206980_selectedRowsChanged,
    inst_evh_6466705138206980_command_nhlcuurn4,
    inst_evh_6466705138206980_command,
    inst_evh_7046827037756168_submit_mrzhbjuvm,
    inst_evh_7046827037756168_submit_x1lgnahb9,
    inst_evh_7046827037756168_submit,
  };

  const evh_7315092382398562_close_0jjyjui79 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_0jjyjui79.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_w4x1g20go = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_w4x1g20go.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_selectedRowsChanged_9pkdkvycs = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_selectedRowsChanged_9pkdkvycs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_command_nhlcuurn4 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_command_nhlcuurn4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_command = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7046827037756168_submit_mrzhbjuvm = async (event, scope) => {
    return await actions.inst_evh_7046827037756168_submit_mrzhbjuvm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7046827037756168_submit_x1lgnahb9 = async (event, scope) => {
    return await actions.inst_evh_7046827037756168_submit_x1lgnahb9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7046827037756168_submit = async (event, scope) => {
    return await actions.inst_evh_7046827037756168_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Payments"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox
      direction="column"
      class="h-100 w-100 overflow-hidden"
      v-if="loaded"
    >
      <div class="h-100 w-100 overflow-hidden position-relative">
        <VueSlickgrid
          name="payments"
          :headerTitle="pgettext('PaymentsView', 'Payments')"
          :items="getItems"
          checkbox-row-selection
          :headerShowColumnSelectorButton="
            b_6466705138206980_headerShowColumnSelectorButton
          "
          :headerShowReloadButton="b_6466705138206980_headerShowReloadButton"
          footer-totals
          export-to-csv
          :exportToCsvFilename="pgettext('ExportFilename', 'payments')"
          :columnDefaults="b_6466705138206980_columnDefaults"
          :columns="b_6466705138206980_columns"
          class="page--grid"
          ref="grid"
          @selectedRowsChanged="
            evh_6466705138206980_selectedRowsChanged($event, {})
          "
          @command="evh_6466705138206980_command($event, {})"
        >
          <template #toolbar>
            <div class="flex-grow-1 d-flex gap-2">
              <PaymentsFilters
                @submit="evh_7046827037756168_submit($event, {})"
              >
              </PaymentsFilters>
            </div>
          </template>
        </VueSlickgrid>
      </div>
    </ItpBox>
  </ItpPage>
</template>
